import React, { useMemo } from 'react';
import { RawTrader, TraderDerivation } from './types';
import {
  rng,
  hash,
  series,
  monthsCalc,
  money,
  sMoney,
  POS,
  NEG,
  INK,
} from './constants';

interface RiskTabProps {
  trader: RawTrader;
  derivation: TraderDerivation;
  riskTone: { fg: string; bg: string; bd: string };
  riskLabel: string;
}

export const RiskTab: React.FC<RiskTabProps> = ({
  trader,
  derivation: d,
  riskTone,
  riskLabel,
}) => {
  const s = useMemo(() => series(trader), [trader]);
  const cut = 65;
  const vis = useMemo(() => s.slice(Math.max(0, s.length - cut)), [s]);
  const lo = useMemo(() => Math.min(...vis, 0), [vis]);
  const hi = useMemo(() => Math.max(...vis, 0.1), [vis]);

  const X = (k: number) => (vis.length > 1 ? ((k / (vis.length - 1)) * 1000).toFixed(1) : '0');

  const r4 = useMemo(() => rng(hash(trader.name) + 61), [trader.name]);
  const loadBars = useMemo(() => {
    return vis
      .filter((_, k) => k % 2 === 0)
      .map(() => ({ h: Math.round((0.15 + r4() * 0.85) * (trader.load / 100) * 180) + 'px' }));
  }, [vis, trader.load, r4]);

  const balLine = useMemo(() => {
    return vis.map((v, k) => X(k) + ',' + (180 - ((v - lo) / (hi - lo)) * 150).toFixed(1)).join(' ');
  }, [vis, lo, hi]);

  const mv = useMemo(() => monthsCalc(trader), [trader]);

  const r5 = useMemo(() => rng(hash(trader.name) + 71), [trader.name]);
  const pnlMonths = useMemo(() => {
    const sc = 92 / (Math.max(...mv.map(Math.abs)) + 1.6);
    const monthLabels = ['T10', 'T11', 'T12', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9'];
    return mv.map((v, i) => {
      const win = Math.abs(v) * (v > 0 ? 1 : 0.55) + 0.4 + r5() * 0.5;
      const loss = (win / trader.pf) * (0.85 + r5() * 0.4);
      return {
        label: monthLabels[i],
        winH: Math.round(win * sc) + 'px',
        mfeH: Math.round(win * sc * 0.35) + 'px',
        lossH: Math.round(loss * sc) + 'px',
        maeH: Math.round(loss * sc * 0.45) + 'px',
      };
    });
  }, [mv, trader.pf, r5]);

  const ratios = [
    {
      kL: 'Lệnh lãi lớn nhất ' + sMoney(d.best),
      kR: 'Lệnh lỗ lớn nhất ' + sMoney(d.worst),
      w: ((d.best / (d.best + Math.abs(d.worst))) * 100).toFixed(0) + '%',
    },
    {
      kL: 'Thắng liên tiếp ' + d.cWins,
      kR: 'Thua liên tiếp ' + d.cLosses,
      w: ((d.cWins / (d.cWins + d.cLosses)) * 100).toFixed(0) + '%',
    },
    {
      kL: 'Chuỗi lãi lớn nhất ' + sMoney(d.avgWin * d.cWins),
      kR: 'Chuỗi lỗ lớn nhất ' + sMoney(-d.avgLoss * d.cLosses),
      w:
        (
          ((d.avgWin * d.cWins) / (d.avgWin * d.cWins + d.avgLoss * d.cLosses)) *
          100
        ).toFixed(0) + '%',
    },
  ];

  const mfe = [
    { k: 'MFE (lãi tối đa trong lệnh)', v: sMoney(d.avgWin * 1.45), c: '#1FA463' },
    { k: 'Lãi trung bình', v: sMoney(d.avgWin), c: '#157A4C' },
    { k: 'Lỗ trung bình', v: sMoney(-d.avgLoss), c: '#E0524D' },
    { k: 'MAE (lỗ tối đa trong lệnh)', v: sMoney(-d.avgLoss * 1.9), c: '#EFB3AE' },
  ];

  const riskGrid = [
    { k: 'Max drawdown', v: trader.dd.toFixed(1) + '%', c: trader.dd > 10 ? NEG : INK, n: money((trader.cap * trader.dd) / 100) },
    { k: 'Drawdown hiện tại', v: trader.curDd.toFixed(1) + '%', c: trader.curDd > 8 ? NEG : INK, n: trader.curDd > 8 ? 'gần mức sâu nhất' : 'trong vùng an toàn' },
    { k: 'Rủi ro / lệnh', v: d.avgRisk.toFixed(2) + '%', c: d.avgRisk > 1 ? NEG : INK, n: 'trung bình 90 ngày' },
    { k: 'Vi phạm giới hạn', v: String(trader.viol), c: trader.viol === 0 ? POS : NEG, n: '90 ngày gần nhất' },
    { k: 'Lỗ ngày lớn nhất', v: money((-trader.cap * trader.dd) / 100 * 0.42), c: NEG, n: 'trong 90 ngày' },
    { k: 'Recovery factor', v: d.recovery.toFixed(2), c: d.recovery >= 1.5 ? POS : INK, n: 'lợi nhuận / drawdown' },
    { k: 'Thua liên tiếp', v: String(d.cLosses), c: NEG, n: 'chuỗi dài nhất' },
    { k: 'Risk / reward', v: '1 : ' + d.rr.toFixed(2), c: INK, n: 'trung bình mỗi lệnh' },
  ];

  const limits = [
    {
      k: 'Giới hạn drawdown tổng (10%)',
      v: trader.dd.toFixed(1) + '% / 10%',
      pct: Math.min((trader.dd / 10) * 100, 100) + '%',
      c: trader.dd > 10 ? '#E0524D' : trader.dd > 7 ? '#E39A2B' : '#1FA463',
      n: trader.dd > 10 ? 'Đã vượt giới hạn' : 'Còn ' + (10 - trader.dd).toFixed(1) + '% khoảng đệm',
    },
    {
      k: 'Giới hạn lỗ ngày (5%)',
      v: (trader.dd * 0.42).toFixed(1) + '% / 5%',
      pct: Math.min(((trader.dd * 0.42) / 5) * 100, 100) + '%',
      c: trader.dd * 0.42 > 5 ? '#E0524D' : '#1FA463',
      n: trader.viol > 0 ? trader.viol + ' lần chạm giới hạn trong 90 ngày' : 'Chưa từng chạm giới hạn',
    },
    {
      k: 'Rủi ro mỗi lệnh (mục tiêu ≤ 1%)',
      v: d.avgRisk.toFixed(2) + '% / 1%',
      pct: Math.min(d.avgRisk * 100, 100) + '%',
      c: d.avgRisk > 1 ? '#E0524D' : '#1FA463',
      n: 'trung bình 90 ngày gần nhất',
    },
    {
      k: 'Số ngày giao dịch tối thiểu (90)',
      v: trader.days + ' / 90',
      pct: Math.min((trader.days / 90) * 100, 100) + '%',
      c: trader.days >= 90 ? '#1FA463' : '#E39A2B',
      n: trader.days >= 90 ? 'Đủ dữ liệu để đánh giá' : 'Chưa đủ dữ liệu để đánh giá ổn định',
    },
  ];

  const curveLabels = ['T5', 'T6', 'T7', 'T8', 'T9'];

  return (
    <div className="flex flex-col gap-4">
      {/* Drawdown & Capital Utilization */}
      <div className="card-box p-4 md:p-5">
        <div className="flex flex-wrap gap-4 items-start justify-between mb-4">
          <div>
            <div className="text-sm font-bold text-gray-900">Drawdown &amp; mức sử dụng vốn</div>
            <div className="text-xs text-[#8B95A3] mt-0.5">
              Cột: % vốn đang chịu rủi ro mỗi ngày · Đường: đường vốn
            </div>
          </div>
          <div className="flex gap-5">
            <div>
              <div className="font-mono text-sm font-semibold text-[#06223B]">{trader.load}%</div>
              <div className="text-[11px] text-[#8B95A3] mt-0.5">Mức sử dụng vốn cao nhất</div>
            </div>
            <div>
              <div className="font-mono text-sm font-semibold text-[#E0524D]">
                {trader.dd.toFixed(1)}%
              </div>
              <div className="text-[11px] text-[#8B95A3] mt-0.5">Drawdown sâu nhất</div>
            </div>
            <div>
              <div
                style={{ color: trader.curDd > 8 ? NEG : INK }}
                className="font-mono text-sm font-semibold"
              >
                {trader.curDd.toFixed(1)}%
              </div>
              <div className="text-[11px] text-[#8B95A3] mt-0.5">Drawdown hiện tại</div>
            </div>
          </div>
        </div>

        <div className="relative h-[190px]">
          <div className="absolute inset-0 flex items-end gap-0.5">
            {loadBars.map((b, idx) => (
              <div key={idx} style={{ height: b.h }} className="flex-1 bg-[#C9D3DE]"></div>
            ))}
          </div>
          <svg viewBox="0 0 1000 190" preserveAspectRatio="none" className="absolute inset-0 w-full h-[190px]">
            <polyline
              points={balLine}
              fill="none"
              stroke="#2F7FE0"
              strokeWidth="1.8"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </div>

        <div className="flex justify-between mt-2 font-mono text-xs text-[#A6AFBC]">
          {curveLabels.map((lb) => (
            <div key={lb}>{lb}</div>
          ))}
        </div>

        <div className="flex gap-4 mt-3 text-xs text-[#5C6779]">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 bg-[#C9D3DE] rounded-sm"></div>Mức sử dụng vốn
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3.5 h-0.5 bg-[#2F7FE0]"></div>Balance
          </div>
        </div>
      </div>

      {/* Profit/Loss Ratios & MFE/MAE */}
      <div className="card-box p-4 md:p-5">
        <div className="text-sm font-bold text-gray-900 mb-5">Biên độ lãi / lỗ</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {ratios.map((r, idx) => (
            <div key={idx}>
              <div className="text-xs font-semibold text-[#157A4C] mb-1.5">{r.kL}</div>
              <div className="flex h-3 rounded overflow-hidden">
                <div
                  style={{ width: r.w }}
                  className="bg-[#D8EFDF] border border-[#93CFA6] rounded-l"
                ></div>
                <div className="flex-1 bg-[#FADED9] border border-[#E8A79E] rounded-r"></div>
              </div>
              <div className="text-xs font-semibold text-[#B03A35] mt-1.5 text-right">{r.kR}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-6 mt-6 pt-5 border-t border-[#EEF1F5]">
          {mfe.map((mItem) => (
            <div key={mItem.k}>
              <div style={{ color: mItem.c }} className="font-mono text-base font-semibold">
                {mItem.v}
              </div>
              <div className="flex items-center gap-1.5 mt-1">
                <div style={{ background: mItem.c }} className="w-2 h-2 rounded-full"></div>
                <span className="text-xs text-[#5C6779]">{mItem.k}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Profit and Loss Stacked Bars */}
      <div className="card-box p-4 md:p-5">
        <div className="text-sm font-bold text-gray-900 mb-0.5">Lãi và lỗ theo tháng</div>
        <div className="text-xs text-[#8B95A3] mb-5">
          Lãi gộp và lỗ gộp thực tế mỗi tháng · vùng nhạt là biên độ lãi/lỗ chưa thực hiện
        </div>
        <div className="flex items-stretch gap-1 h-[230px]">
          {pnlMonths.map((mItem) => (
            <div key={mItem.label} className="flex-1 flex flex-col">
              <div className="flex-1 flex flex-col justify-end">
                <div style={{ height: mItem.mfeH }} className="bg-[#C7E7D2]"></div>
                <div style={{ height: mItem.winH }} className="bg-[#1FA463]"></div>
              </div>
              <div className="h-px bg-[#E7EBF0]"></div>
              <div className="flex-1">
                <div style={{ height: mItem.lossH }} className="bg-[#E0524D]"></div>
                <div style={{ height: mItem.maeH }} className="bg-[#F6C9C4]"></div>
              </div>
              <div className="font-mono text-[9px] text-center text-[#A6AFBC] mt-1.5">{mItem.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Grid & Limits Compliance */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        <div className="card-box p-4 md:p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="text-sm font-bold text-gray-900">Kiểm soát rủi ro</div>
            <span
              style={{ background: riskTone.bg, color: riskTone.fg }}
              className="text-xs font-bold py-1 px-2.5 rounded-md"
            >
              RỦI RO {riskLabel.toUpperCase()}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {riskGrid.map((c) => (
              <div key={c.k} className="bg-[#F7F9FB] rounded-lg p-3">
                <div className="text-[11px] text-[#5C6779] mb-1">{c.k}</div>
                <div style={{ color: c.c }} className="font-mono text-sm font-semibold">
                  {c.v}
                </div>
                <div className="text-[10px] text-[#A6AFBC] mt-0.5">{c.n}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="card-box p-4 md:p-5">
          <div className="text-sm font-bold text-gray-900 mb-0.5">Tuân thủ giới hạn</div>
          <div className="text-xs text-[#8B95A3] mb-4">
            Trạng thái rủi ro được tính từ các ngưỡng dưới đây, không phải khuyến nghị đầu tư
          </div>
          <div className="flex flex-col">
            {limits.map((lItem) => (
              <div key={lItem.k} className="py-3 border-b border-[#F3F5F8] last:border-b-0">
                <div className="flex justify-between items-baseline gap-2.5 mb-1.5">
                  <span className="text-xs text-[#3F4A5A]">{lItem.k}</span>
                  <span style={{ color: lItem.c }} className="font-mono text-xs font-semibold whitespace-nowrap">
                    {lItem.v}
                  </span>
                </div>
                <div className="h-1.5 bg-[#F0F3F7] rounded-full overflow-hidden">
                  <div style={{ width: lItem.pct, background: lItem.c }} className="h-full rounded-full"></div>
                </div>
                <div className="text-[10.5px] text-[#A6AFBC] mt-1.5">{lItem.n}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
